import { toggleToast } from '@bilibili-maoer/prelude'

import { useState } from 'react'
import { Controller } from 'react-hook-form'

import { LoadingButton } from '@mui/lab'
import {
  Box,
  ButtonGroup,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Link as MuiLink,
  Select,
  Stack,
  TextField,
  styled,
} from '@mui/material'
import { P, match } from 'ts-pattern'
import { ConditionalKeys } from 'type-fest'
import { z } from 'zod'

import { Typo } from '~/core/components/alias'
import { DialogButton } from '~/core/components/dialog-button'
import { EObjectiveType } from '~/core/enums'
import { useZodForm } from '~/core/utils/use-zod-form'
import { EContractStatus, EDurationID } from '~/features/guild/contract/enums'
import { ContractInput } from '~/features/guild/contract/schemas'
import { formatContractTime } from '~/features/guild/contract/utils'
import { Links } from '~/router/links'
import { GuildApi } from '~/services/fm/guild'
import { BindMobile } from '~/shared/components/bind-mobile'
import { SafeFullPage } from '~/shared/components/full-page'
import { VerificationCode } from '~/shared/components/verification-code'
import AppLink from '~/shared/navigation/app-link'
import { Seconds } from '~/shared/types/base/units'

const StyledButton = styled(LoadingButton)`
  height: 4rem;
`

const Agreement = (props: { checked: boolean; onClick: () => void }) => (
  <FormControlLabel
    control={<Checkbox size="small" checked={props.checked} onClick={props.onClick} />}
    label={
      <Typo fontSize="1.2rem" color="text.secondary">
        {'我已阅读并接受 '}
        <MuiLink
          component={AppLink}
          to={Links.CONTRACT_SERVICE_AGREEMENT}
          target="_blank"
          sx={(theme) => ({
            color: theme.palette.text.primary,
            textDecorationColor: theme.palette.text.primary,
          })}
        >
          公会主播入驻服务协议
        </MuiLink>
      </Typo>
    }
  />
)

const showTime = (data: ContractDetailsData, what: ConditionalKeys<Required<ContractDetailsData>, Seconds>) => {
  const time = data[what]
  return time === undefined ? '' : formatContractTime(time)
}

const durationOptions = [
  { value: EDurationID.ThreeYears, text: '3 年' },
  { value: EDurationID.FiveYears, text: '5 年', defaultSelected: true },
]

type Source = 'apply' | 'invite'

type Decision = 'reject' | 'accept'
const decisionText = (decision: Decision) =>
  ({
    reject: '拒绝',
    accept: '接受',
  }[decision])

type Operator = 'join' | 'renew'
const operatorText = (operator: Operator) =>
  ({
    join: '签约',
    renew: '续约',
  }[operator])

const descriptionText = (data: ContractDetailsData, source: Source, operator: Operator) =>
  match({ source, operator })
    .with({ source: 'invite' }, () => `请在 ${showTime(data, 'expireTime')} 前处理，逾期将自动拒绝`)
    .with({ operator: 'join' }, () => '提交申请 7 天后，若公会未处理，该申请将自动失效')
    .with({ operator: 'renew' }, () => '若合约结束前公会未处理，合约到期时将自动解约')
    .exhaustive()

export interface ContractDetailsData {
  guildId: number
  guildName: string
  masterId: number
  duration?: string
  rateString: string
  settlement: string
  createTime?: Seconds
  expireTime?: Seconds
  extendedTime?: Seconds
  status?: EContractStatus
}

export interface ContractDetailsProps {
  data: ContractDetailsData
  source: Source
  operator: Operator
  isLoading?: boolean
  onSubmit?: (input: z.infer<typeof ContractInput>, data: ContractDetailsData) => Promise<unknown>
  onReject?: (input: z.infer<typeof ContractInput>, data: ContractDetailsData) => Promise<unknown>
}

export const ContractDetails = (props: ContractDetailsProps) => {
  const [dialog, setDialog] = useState<{ decision: Decision }>()
  const [agree, setAgree] = useState<boolean>(false)
  // 后端不支持重复验证同一个验证码，所以需要记录上一次验证成功的验证码用来跳过不需要验证的情况
  const [lastValidCode, setLastValidCode] = useState<string>()
  const { data: mobile } = GuildApi.getUserMobile.useQuery()
  const [triggerSetUserPermission] = GuildApi.setUserPermission.useMutation()
  const form = useZodForm(ContractInput, {
    defaultValues: {
      duration: EDurationID.FiveYears,
    },
  })

  const handleSubmit = (onValid: (input: z.infer<typeof ContractInput>) => unknown, { requireAgree = false } = {}) =>
    form.handleSubmit(async (input) => {
      if (requireAgree && !agree) {
        toggleToast('请先阅读并同意公会主播入驻服务协议')
        return
      }

      if (lastValidCode === input.code) {
        onValid(input)
        return
      }

      const { state, info } = await triggerSetUserPermission({
        identify_code: input.code,
        objective_type: EObjectiveType.ImportantOperation,
      }).unwrap()

      if (state === 'fail') {
        form.setError('code', { message: info })
        return
      }

      setLastValidCode(input.code)
      onValid(input)
    })

  return (
    <SafeFullPage>
      <Box component="form" height="1">
        <Box display="flex" flexDirection="column" height="1">
          {/* Content */}
          <Container sx={{ flex: 1, pt: '1.6rem', pb: '3.2rem', overflow: 'auto' }}>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>公会名称</FormLabel>
                <TextField disabled value={props.data.guildName} />
              </FormControl>

              <FormControl>
                <FormLabel>会长M号</FormLabel>
                <TextField disabled value={props.data.masterId} />
              </FormControl>

              {props.data.duration === undefined ? (
                <FormControl>
                  <FormLabel>签约时限</FormLabel>
                  <Select defaultValue={form.formState.defaultValues?.duration} inputProps={form.register('duration')}>
                    {durationOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.text}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <>
                  <FormControl>
                    <FormLabel>签约时限</FormLabel>
                    <TextField disabled value={props.data.duration} />
                  </FormControl>
                  {!!props.data.extendedTime && (
                    <FormControl>
                      <FormLabel>合约延长至</FormLabel>
                      <TextField disabled value={showTime(props.data, 'extendedTime')} />
                    </FormControl>
                  )}
                </>
              )}

              <FormControl>
                <FormLabel>最低分成比例</FormLabel>
                <TextField disabled value={props.data.rateString} />
              </FormControl>

              <FormControl>
                <FormLabel>结算方式</FormLabel>
                <TextField disabled value={props.data.settlement} />
              </FormControl>

              {props.data.createTime !== undefined && (
                <FormControl>
                  <FormLabel>邀请时间</FormLabel>
                  <TextField disabled value={showTime(props.data, 'createTime')} />
                </FormControl>
              )}
            </Stack>

            <Agreement checked={agree} onClick={() => setAgree((state) => !state)} />

            <Stack spacing={1}>
              {mobile && (
                <Typo color="text.secondary" fontSize="1.1rem">
                  {match(mobile)
                    .with(
                      { state: 'success', mobile: P.string },
                      ({ mobile }) => `为验证您的身份，请向您的手机（${mobile}）发送验证码`
                    )
                    .with({ state: 'not_bind' }, () => (
                      <>
                        为验证您的身份，请先
                        <BindMobile>绑定手机号</BindMobile>
                      </>
                    ))
                    .exhaustive()}
                </Typo>
              )}
              <Controller
                name="code"
                defaultValue=""
                control={form.control}
                render={({ field }) => (
                  <VerificationCode
                    slotProps={{ input: field }}
                    onSendCode={() => setLastValidCode(undefined)}
                    helperText={form.formState.errors.code?.message}
                  />
                )}
              />
            </Stack>
          </Container>

          {/* Actions */}
          <Box
            sx={{
              pt: '1rem',
              pb: '1rem',
              boxShadow: '0 -2px 4px -2px rgba(0, 0, 0, 0.3)',
            }}
          >
            <Container>
              <Stack spacing={1}>
                <Typo textAlign="center" color="text.secondary" fontSize="1.1rem">
                  {descriptionText(props.data, props.source, props.operator)}
                </Typo>
                <ButtonGroup fullWidth>
                  {match(props.source)
                    .with('apply', () => (
                      <StyledButton
                        variant="contained"
                        loading={props.isLoading}
                        onClick={handleSubmit(
                          (input) => {
                            setLastValidCode(undefined)
                            props.onSubmit?.(input, props.data)
                          },
                          { requireAgree: true }
                        )}
                      >
                        申请{operatorText(props.operator)}
                      </StyledButton>
                    ))
                    .with(
                      'invite',
                      () =>
                        props.data.status === EContractStatus.Unprocessed && (
                          <>
                            <StyledButton
                              variant="outlined"
                              onClick={handleSubmit(() => setDialog({ decision: 'reject' }))}
                            >
                              拒绝
                            </StyledButton>
                            <StyledButton
                              variant="contained"
                              onClick={handleSubmit(() => setDialog({ decision: 'accept' }), { requireAgree: true })}
                            >
                              接受
                            </StyledButton>
                          </>
                        )
                    )
                    .exhaustive()}
                </ButtonGroup>
              </Stack>
            </Container>
          </Box>
        </Box>
      </Box>
      <Dialog open={dialog !== undefined} onClose={() => setDialog(undefined)}>
        {dialog !== undefined && (
          <>
            <DialogContent>
              <DialogContentText>
                确认{decisionText(dialog.decision)}公会{operatorText(props.operator)}邀请吗？
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <DialogButton variant="secondary" onClick={() => setDialog(undefined)}>
                取消
              </DialogButton>
              <DialogButton
                variant="primary"
                loading={props.isLoading}
                onClick={async () => {
                  setLastValidCode(undefined)
                  await match(dialog.decision)
                    .with('accept', () => props.onSubmit?.(form.getValues(), props.data))
                    .with('reject', () => props.onReject?.(form.getValues(), props.data))
                    .exhaustive()
                  setDialog(undefined)
                }}
              >
                {decisionText(dialog.decision)}
              </DialogButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </SafeFullPage>
  )
}
